import api from '../../utils/helpers/api'
import axios from 'axios'
import type {
  LaporanKegiatan,
  LaporanKegiatanParams,
  AktivitasPenugasan,
  AktivitasPenugasanFormData,
} from '../../types/pana'

type ResponType = {
  data: any
  meta: object
}

type ExportExcelParams = {
  uraian: string
}

type UploadExcelParams = {
  file: File
  preview?: 0 | 1
}

type ImportInsentifError = {
  excel_row: number
  nik: string
  email: string
  nama_penerima: string
  uraian: string
  errors: string[]
}

type ImportInsentifResponse = {
  success_count: number
  error_count: number
  errors: ImportInsentifError[]
}

const panaAdmin = {
  /**
   * MONITORING SUPER ADMIN
   */

  /**
   * Get monitoring data untuk semua user PANA
   * @param params - Pagination, sorting, filtering parameters
   */
  getMonitoring: async (params?: {
    page?: number
    per_page?: number
    sort_field?: string
    sort_order?: 'asc' | 'desc'
    search?: string
  }): Promise<ResponType> => {
    return (await api.get('/monitoring-superadmin', {params})) as Promise<ResponType>
  },

  /**
   * Get waktu publish untuk monitoring
   */
  getPublishTime: async (params?: any): Promise<ResponType> => {
    return (await api.get('/monitoring-superadmin/waktu-publish', {params})) as Promise<
      ResponType
    >
  },

  /**
   * Save waktu publish
   */
  savePublishTime: async (data: any): Promise<ResponType> => {
    return (await api.post('/monitoring-superadmin/waktu-publish', data)) as Promise<ResponType>
  },

  /**
   * Change presentation status
   */
  changePresentation: async (data: any): Promise<ResponType> => {
    return (await api.post('/monitoring-superadmin/change-presention', data)) as Promise<
      ResponType
    >
  },

  /**
   * Download all files from proposal detail
   */
  downloadAllFiles: async (panaId: number): Promise<ResponType> => {
    return (await api.get(
      `/monitoring-superadmin/download-all-files-detail-proposal/${panaId}`
    )) as Promise<ResponType>
  },

  /**
   * Get detail proposal admin view
   */
  getProposalDetail: async (registerId: number): Promise<ResponType> => {
    return (await api.get(`/monitoring-superadmin/proposal-detail/${registerId}`)) as Promise<
      ResponType
    >
  },

  /**
   * Get detail wawancara admin view
   */
  getInterviewDetail: async (registerId: number): Promise<ResponType> => {
    return (await api.get(`/monitoring-superadmin/detail-wawancara/${registerId}`)) as Promise<
      ResponType
    >
  },

  /**
   * Get log perubahan status
   */
  getStatusLog: async (params?: any): Promise<ResponType> => {
    return (await api.get('/monitoring-superadmin/status-log', {params})) as Promise<ResponType>
  },

  /**
   * LOCUS
   */

  /**
   * Get all locus options for dropdown filter
   * GET /api/v1/locus?per_page=20
   */
  getLocus: async (): Promise<ResponType> => {
    return (await api.get('/locus', {params: {per_page: 20}})) as Promise<ResponType>
  },

  /**
   * BATCH
   */

  /**
   * Get all batch options for dropdown filter
   * GET /api/v1/pana/batch?per_page=20
   */
  getBatch: async (): Promise<ResponType> => {
    return (await api.get('/pana/batch', {params: {per_page: 20}})) as Promise<ResponType>
  },

  /**
   * LAPORAN KEGIATAN - ADMIN VIEW (ALL USERS)
   */

  /**
   * Get all laporan kegiatan from all users (admin view)
   * @param params - Pagination, filter, search, sort (see LaporanKegiatanParams)
   */
  getAllLaporanKegiatan: async (params?: LaporanKegiatanParams): Promise<ResponType> => {
    return (await api.get('/pana-laporan-kegiatan', {params})) as Promise<ResponType>
  },

  /**
   * Approval/Reject laporan kegiatan
   * @param data - Contains pana_laporan_kegiatan_id, status (1=approved, 2=rejected), catatan
   */
  approvalLaporan: async (data: {
    pana_laporan_kegiatan_id: number
    status: 0 | 1 | 2
    catatan?: string
  }): Promise<ResponType> => {
    return (await api.post('/approval', data)) as Promise<ResponType>
  },

  /**
   * Get detail laporan kegiatan by ID (admin view)
   */
  getLaporanDetail: async (id: number): Promise<ResponType> => {
    return (await api.get(`/pana-laporan-kegiatan/${id}`)) as Promise<ResponType>
  },

  /**
   * Delete laporan kegiatan (admin)
   */
  deleteLaporanKegiatan: async (id: number): Promise<ResponType> => {
    return (await api.delete(`/pana-laporan-kegiatan/${id}`)) as Promise<ResponType>
  },

  /**
   * Export Excel laporan kegiatan
   * @param params - Filter parameters (id_batch, step, is_lulus, storage_base_url)
   * @returns Response with data.file as Base64 data URL and data.name as filename
   */
  exportLaporanKegiatanExcel: async (params?: {
    id_batch?: number
    step?: number
    is_lulus?: 0 | 1
    storage_base_url?: string
  }): Promise<any> => {
    return await api.get('/pana-laporan-kegiatan/export-excel', {params})
  },

  /**
   * Export Excel laporan kegiatan (index filters — same as list UI)
   * GET /api/v1/pana-laporan-kegiatan/export-excel-new
   * @returns Response with data.file (Base64 data URL), data.name, data.row_count
   */
  exportLaporanKegiatanExcelNew: async (
    params?: Omit<LaporanKegiatanParams, 'page' | 'per_page'>
  ): Promise<ResponType> => {
    return (await api.get('/pana-laporan-kegiatan/export-excel-new', {
      params,
    })) as Promise<ResponType>
  },

  /**
   * AKTIVITAS PENUGASAN - ADMIN CRUD
   */

  /**
   * Create aktivitas penugasan (admin only)
   */
  createAktivitas: async (data: AktivitasPenugasanFormData): Promise<ResponType> => {
    return (await api.post('/pana-aktivitas-penugasan', data)) as Promise<ResponType>
  },

  /**
   * Update aktivitas penugasan (admin only)
   * Note: API menggunakan POST, bukan PUT
   */
  updateAktivitas: async (
    id: number,
    data: Partial<AktivitasPenugasanFormData>
  ): Promise<ResponType> => {
    return (await api.post(`/pana-aktivitas-penugasan/${id}`, data)) as Promise<ResponType>
  },

  /**
   * Delete aktivitas penugasan (admin only)
   */
  deleteAktivitas: async (id: number): Promise<ResponType> => {
    return (await api.delete(`/pana-aktivitas-penugasan/${id}`)) as Promise<ResponType>
  },

  /**
   * Toggle status aktivitas (active/inactive)
   */
  toggleAktivitasStatus: async (id: number): Promise<ResponType> => {
    return (await api.post(`/pana-aktivitas-penugasan/${id}/toggle-status`)) as Promise<
      ResponType
    >
  },

  /**
   * INDIKATOR PENILAIAN (Assessment Indicator)
   */

  /**
   * Get list indikator penilaian
   */
  getAssessmentIndicators: async (params?: {
    page?: number
    per_page?: number
    sort_field?: string
    sort_order?: 'asc' | 'desc'
  }): Promise<ResponType> => {
    return (await api.get('/master/proposal-aspek', {params})) as Promise<ResponType>
  },

  /**
   * Create indikator penilaian
   */
  createAssessmentIndicator: async (data: any): Promise<ResponType> => {
    return (await api.post('/master/proposal-aspek', data)) as Promise<ResponType>
  },

  /**
   * Update indikator penilaian
   */
  updateAssessmentIndicator: async (id: number, data: any): Promise<ResponType> => {
    return (await api.put(`/master/proposal-aspek/${id}`, data)) as Promise<ResponType>
  },

  /**
   * Get detail verifikator
   */
  getVerificatorDetail: async (id: number): Promise<ResponType> => {
    return (await api.get(`/penilaian/verifikator/${id}`)) as Promise<ResponType>
  },

  /**
   * EXCEL EXPORT/IMPORT
   */

  /**
   * Download Excel template untuk penempatan
   * Response: Base64 string
   */
  downloadAssessmentExcel: async (): Promise<any> => {
    return await api.get('/user/pana-user/assessment-excel-with-penempatan')
  },

  /**
   * Upload Excel penilaian manual
   * @param file - Excel file
   * @param preview - 0 = save, 1 = preview only
   */
  uploadAssessmentExcel: async (file: File, preview: 0 | 1 = 0): Promise<ResponType> => {
    const formData = new FormData()
    formData.append('file', file)

    return (await api.post(
      `/user/pana-user/upload-penilaian-manual-excel?preview=${preview}`,
      formData
    )) as Promise<ResponType>
  },

  /**
   * Export Excel insentif ERP
   * Response: Base64 string in data URI format
   */
  exportInsentifExcel: async (data: ExportExcelParams): Promise<any> => {
    return await api.post('/user/pana-user/export-insentif-excel', data)
  },

  /**
   * Import Excel insentif yang sudah diedit
   * @param file - Excel file (.xlsx/.xls/.csv, max 10MB)
   * @returns Import result with success/error count and detailed errors per row
   */
  importInsentifExcel: async (file: File): Promise<ResponType> => {
    const formData = new FormData()
    formData.append('file', file)
    // Browser akan auto-set Content-Type: multipart/form-data dengan boundary
    return (await api.post('/user/pana-user/import-insentif-excel', formData)) as Promise<ResponType>
  },

  /**
   * PAKTA INTEGRITAS
   */

  /**
   * Generate pakta integritas
   */
  generateIntegrityFacts: async (data: any): Promise<ResponType> => {
    return (await api.post('/pana/generate-pakta-integritas', data)) as Promise<ResponType>
  },

  /**
   * Check pakta integritas
   */
  checkIntegrityFacts: async (): Promise<ResponType> => {
    return (await api.get('/pana/check-pakta-integritas')) as Promise<ResponType>
  },

  /**
   * BUKU PANDUAN
   */

  /**
   * Get list buku panduan (admin)
   */
  getBukuPanduanList: async (params?: {
    page?: number
    per_page?: number
  }): Promise<ResponType> => {
    return (await api.get('/admin/file-buku-panduan', {params})) as Promise<ResponType>
  },

  /**
   * Get list buku panduan dengan hash ID
   */
  getBukuPanduanListHash: async (params?: {
    page?: number
    per_page?: number
  }): Promise<ResponType> => {
    return (await api.get('/admin/file-buku-panduan/list-hash', {params})) as Promise<ResponType>
  },

  /**
   * Get detail buku panduan by ID
   */
  getBukuPanduanById: async (id: string | number): Promise<ResponType> => {
    return (await api.get(`/admin/file-buku-panduan/${id}`)) as Promise<ResponType>
  },

  /**
   * Create buku panduan
   */
  createBukuPanduan: async (data: FormData): Promise<ResponType> => {
    return (await api.post('/admin/file-buku-panduan', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })) as Promise<ResponType>
  },

  /**
   * Update buku panduan
   */
  updateBukuPanduan: async (id: string | number, data: FormData): Promise<ResponType> => {
    return (await api.post(`/admin/file-buku-panduan/${id}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })) as Promise<ResponType>
  },

  /**
   * Delete buku panduan
   */
  deleteBukuPanduan: async (id: string | number): Promise<ResponType> => {
    return (await api.delete(`/admin/file-buku-panduan/${id}`)) as Promise<ResponType>
  },

  /**
   * Download buku panduan (public)
   */
  downloadBukuPanduan: async (tipe: string): Promise<any> => {
    return await api.get('/admin/file-buku-panduan/download', {
      params: { tipe },
    })
  },

  getBukuPanduanActive: async (): Promise<any> => {
    const response: any = await api.get('/admin/file-buku-panduan/list-hash')
    // Response structure: response.data.data (same as admin page)
    const data = response?.data?.data || response?.data || response
    if (Array.isArray(data)) {
      return data.filter((item: any) => item.is_active == 1)
    }
    return []
  },
}

export default panaAdmin